/**
 * Theme V2 Motion Preset Schemes (schema 2.17 animation, theme plane)
 *
 * Curated motion bundles the theme editor offers instead of raw 35-easing /
 * 77-preset pickers (Material M3 "motion scheme" pattern). Selecting a scheme
 * fills concrete `ThemeV2AnimationSettings` values; the user may then tweak
 * them in Advanced — divergence flips the selection to `custom`.
 *
 * Value ranges follow motion best practices: screen transitions 200–500ms,
 * entrance slightly longer with per-slot stagger; Paywall gets a calm
 * per-kind override (modal-feel coverVertical + plain fade) so prices never
 * bounce.
 *
 * DEFERRED SCOPE (v1 cut, user decision 2026-07-06): list item animations and
 * button enter/idle/tap animations are intentionally NOT part of the scheme —
 * they stay per-screen settings in the screen editor.
 */

import type { ScreenAnimation, ScreenTransition } from '@segmently/schema-models/v2';
import type {
  ThemeV2AnimationSettings,
  ThemeV2MotionPresetId,
} from '@modules/themes/types/themesV2.types';

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

// ---------------------------------------------------------------------------
// Option lists for the editor selects
// ---------------------------------------------------------------------------

/**
 * Transition types the web-shell runtime actually implements.
 * ⚠️ Keep in sync with the switch in
 * `web-shell/src/engine/transitions/transitionSpec.ts` (buildTransitionSpec)
 * — spec'd-but-unimplemented types (flip, sharedAxis, none) resolve to an
 * instant swap there and must not be offered in the theme UI.
 */
export const THEME_TRANSITION_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'slide', label: 'Slide' },
  { value: 'push', label: 'Push' },
  { value: 'cover', label: 'Cover' },
  { value: 'coverVertical', label: 'Cover Vertical' },
  { value: 'reveal', label: 'Reveal' },
  { value: 'fade', label: 'Fade' },
  { value: 'crossfade', label: 'Crossfade' },
  { value: 'fadeThrough', label: 'Fade Through' },
  { value: 'scale', label: 'Scale' },
];

/**
 * Curated easing subset (M3-style: standard / emphasized / decelerate /
 * accelerate variants). Values are EASING_MAP keys — both easing maps
 * (`screen-view-v2/animations/easing.ts` and web-shell `transitionSpec.ts`)
 * resolve them; do NOT add bounce/elastic keys (unsupported, degrade to
 * 'ease' with a console warning). `css` mirrors the EASING_MAP cubic-bezier
 * for these 7 keys only — used by the editor-local TransitionPreviewDemo.
 */
export const THEME_EASING_OPTIONS: ReadonlyArray<{ value: string; label: string; css: string }> = [
  { value: 'easeOut', label: 'Ease Out', css: 'ease-out' },
  { value: 'easeInOut', label: 'Ease In-Out', css: 'ease-in-out' },
  { value: 'easeInOutCubic', label: 'Ease In-Out (Cubic)', css: 'cubic-bezier(0.65, 0, 0.35, 1)' },
  { value: 'easeOutCubic', label: 'Ease Out (Cubic)', css: 'cubic-bezier(0.33, 1, 0.68, 1)' },
  { value: 'easeOutExpo', label: 'Ease Out (Expo)', css: 'cubic-bezier(0.16, 1, 0.3, 1)' },
  { value: 'easeOutBack', label: 'Ease Out (Back)', css: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
  { value: 'linear', label: 'Linear', css: 'linear' },
];

/** CSS timing-function for a curated easing key (demo-local; default ease-out). */
export function themeEasingToCss(easing: string | undefined): string {
  return THEME_EASING_OPTIONS.find((o) => o.value === easing)?.css ?? 'ease-out';
}

// ---------------------------------------------------------------------------
// Scheme bundles
// ---------------------------------------------------------------------------

function transition(type: string, durationMs: number, easing: string): ScreenTransition {
  return {
    type: type as ScreenTransition['type'],
    timing: { type: 'tween', duration: durationMs, easing: easing as never },
  };
}

function entrance(preset: string, durationMs: number, staggerMs: number): ScreenAnimation {
  return {
    enterAnimation: {
      preset: preset as never,
      timing: { type: 'tween', duration: durationMs },
    },
    ...(staggerMs > 0 ? { stagger: { delayPerItem: staggerMs } } : {}),
  };
}

/**
 * Transition types whose runtime spec consumes `direction`
 * (buildTransitionSpec DIRECTION_VECTORS path). Fades/scale ignore it —
 * keep in sync with web-shell transitionSpec.ts (buildTransitionSpec).
 */
export const THEME_DIRECTIONAL_TRANSITION_TYPES = new Set(['slide', 'push', 'cover', 'coverVertical', 'reveal']);

/** Direction options (where the visible content moves TOWARD; iOS push = Left). */
export const THEME_TRANSITION_DIRECTION_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
] as const;

/** Calm Paywall override shared by the Smooth/Dynamic/Playful schemes. */
export const THEME_PAYWALL_CALM_OVERRIDE = {
  transition: transition('coverVertical', 400, 'easeInOutCubic'),
  screenAnimation: entrance('fadeIn', 300, 0),
} as const;

export interface ThemeMotionPresetDefinition {
  id: Exclude<ThemeV2MotionPresetId, 'custom'>;
  label: string;
  /** One-line value summary shown on the scheme card. */
  description: string;
  settings: ThemeV2AnimationSettings;
}

/**
 * The 5 curated schemes. `none` explicitly nulls motion (distinct from an
 * absent `animationSettings`, which means "theme does not manage motion").
 */
export const THEME_MOTION_PRESETS: ReadonlyArray<ThemeMotionPresetDefinition> = [
  {
    id: 'none',
    label: 'None',
    description: 'No motion. Instant screens.',
    settings: {
      motionPreset: 'none',
      transition: null,
      screenAnimation: null,
    },
  },
  {
    id: 'subtle',
    label: 'Subtle',
    description: 'Fade 250ms · fadeIn',
    settings: {
      motionPreset: 'subtle',
      transition: transition('fade', 250, 'easeOut'),
      screenAnimation: entrance('fadeIn', 300, 0),
    },
  },
  {
    id: 'smooth',
    label: 'Smooth',
    description: 'Slide 300ms · fadeInUp, stagger 80',
    settings: {
      motionPreset: 'smooth',
      transition: transition('slide', 300, 'easeInOutCubic'),
      screenAnimation: entrance('fadeInUp', 450, 80),
      perKindOverrides: { Paywall: cloneJson(THEME_PAYWALL_CALM_OVERRIDE) },
    },
  },
  {
    id: 'dynamic',
    label: 'Dynamic',
    description: 'Push 280ms · slideInUp, stagger 60',
    settings: {
      motionPreset: 'dynamic',
      transition: transition('push', 280, 'easeOutExpo'),
      screenAnimation: entrance('slideInUp', 400, 60),
      perKindOverrides: { Paywall: cloneJson(THEME_PAYWALL_CALM_OVERRIDE) },
    },
  },
  {
    id: 'playful',
    label: 'Playful',
    description: 'Scale 350ms bounce · zoomIn, stagger 100',
    settings: {
      motionPreset: 'playful',
      transition: transition('scale', 350, 'easeOutBack'),
      screenAnimation: entrance('zoomIn', 500, 100),
      perKindOverrides: { Paywall: cloneJson(THEME_PAYWALL_CALM_OVERRIDE) },
    },
  },
];

/** Default scheme for a fresh Animations setup. */
export const DEFAULT_THEME_MOTION_PRESET_ID: ThemeV2MotionPresetId = 'smooth';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Deep-cloned settings for a scheme id; null for unknown/custom. */
export function expandMotionPreset(id: ThemeV2MotionPresetId): ThemeV2AnimationSettings | null {
  const def = THEME_MOTION_PRESETS.find((p) => p.id === id);
  return def ? cloneJson(def.settings) : null;
}

function normalizedMotionValues(settings: ThemeV2AnimationSettings): string {
  // motionPreset is a UI label, not a value — compare only the material fields.
  const { transition: t, screenAnimation: s, perKindOverrides } = settings;
  return JSON.stringify({ t: t ?? null, s: s ?? null, o: perKindOverrides ?? null });
}

/**
 * Which scheme the current values correspond to; 'custom' when they diverge
 * from every scheme (drives the implicit Custom chip in the section header).
 */
export function resolveMotionPresetId(
  settings: ThemeV2AnimationSettings | null | undefined
): ThemeV2MotionPresetId | null {
  if (!settings) return null;
  const current = normalizedMotionValues(settings);
  const match = THEME_MOTION_PRESETS.find((p) => normalizedMotionValues(p.settings) === current);
  return match ? match.id : 'custom';
}
